import debounce from 'lodash.debounce';
import { _getLogger } from '@doxyme/analytics';
import {
  DeviceKind,
  MediaEventType,
  MediaStorageKeys,
} from '../media/media.constants';
import type {
  CameraId,
  FireFnType,
  MicrophoneId,
  SelectedDevices,
  SelectedDevicesChangeEvent,
  SpeakerId,
} from '../media/media.types';
import type { Logger } from 'loglevel';
import {
  findDefaultDevice,
  findDeviceById,
  getExplicitIdForGroup,
  hasDevice,
  isDifferentMic,
  shouldKeepDeviceSelected,
} from './devices.utils';
import { storage } from '@doxyme/ui/utils';

/**
 * `Devices` class is responsible for managing user media devices such as
 * cameras, microphones and audio output devices such as speakers and headphones.
 *
 * It provides methods to initialize, subscribe, and set prefered devices.
 * It also includes getter methods to retrieve available cameras, microphones, and speakers.
 *
 * When devices are updated or selected a MediaEventType.DevicesChange or
 * MediaEventType.SelectedDevicesChange event will be fired.
 *
 * This class is instantiated and used by the Media class.
 *
 * @class Devices
 */
export class Devices {
  static readonly deviceChangeEvent = 'devicechange'; // this browser API, just to not have plain string in code.

  private _devices: MediaDeviceInfo[] = [];

  private readonly _selected: SelectedDevices = {};

  private devicesHash = '';

  private readonly fire: FireFnType;

  /**
   * The groupId of the selected microphone
   *
   * We need to remember the mic group to be able to differentiate between default devices.
   **/
  private microphoneGroupId?: string;

  // debounce because it can be fired multiple times, write to prop to have the referance for unsubscribing

  private readonly deviceChangeListener = debounce((): void => {
    void this.devicesChangeHandler();
  }, 100);

  constructor(fire: FireFnType) {
    this.fire = fire;
  }

  /**
   * Ensures that `_getLogger` is executed at runtime to avoid circular
   * dependency issue with initialization order of logger with ESM
   * module exports.
   * @memberof Devices
   */
  private static readonly getLogger = (): Logger => _getLogger('Devices');

  static async enumerateDevices(): Promise<MediaDeviceInfo[]> {
    try {
      return await window.navigator.mediaDevices.enumerateDevices();
    } catch (error) {
      Devices.getLogger().error('[Devices/getEnumerateDevices]:', error);
      return [] as MediaDeviceInfo[];
    }
  }

  get selected(): SelectedDevices {
    return this._selected;
  }

  get devices(): MediaDeviceInfo[] {
    return this._devices.filter((device) => device.label);
  }

  get cameras(): MediaDeviceInfo[] {
    return this.devices.filter(
      (device) => device.kind === DeviceKind.VideoInput,
    );
  }

  get microphones(): MediaDeviceInfo[] {
    return this.devices.filter(
      (device) => device.kind === DeviceKind.AudioInput,
    );
  }

  get speakers(): MediaDeviceInfo[] {
    return this.devices.filter(
      (device) => device.kind === DeviceKind.AudioOutput,
    );
  }

  get hasCameras(): boolean {
    return this._devices.some(
      (device) => device.kind === DeviceKind.VideoInput,
    );
  }

  get hasMicrophones(): boolean {
    return this._devices.some(
      (device) => device.kind === DeviceKind.AudioInput,
    );
  }

  async init(): Promise<void> {
    Devices.getLogger().info('[Devices/init]');

    this.subscribe();
    this._devices = await Devices.enumerateDevices();
    this.restoreSelectedDevices();
    this.selectDefaultDevices();
    this.fire([{ type: MediaEventType.DevicesChange }]);
  }

  subscribe(): void {
    window.navigator.mediaDevices.addEventListener(
      Devices.deviceChangeEvent,
      this.deviceChangeListener,
    );
  }

  unsubscribe(): void {
    window.navigator.mediaDevices.removeEventListener(
      Devices.deviceChangeEvent,
      this.deviceChangeListener,
    );
  }

  /**
   * Select the camera to be used and save the preference to localstorage
   * @param id cameraId
   * @param savePreference — remember in localStorage (default=true)
   */
  setCamera(id: CameraId, savePreference = true): void {
    this.setSelectedDevice('cameraId', id, savePreference);
  }

  /**
   * Select the microphone to be used and save the preference to localstorage
   * @param id microphoneId
   * @param savePreference should this device be saved in localstorage
   */
  setMicrophone(id: MicrophoneId, savePreference = true): void {
    Devices.getLogger().info('[Devices/setMicrophone]', { id, savePreference });

    const newDevice = findDeviceById(id, this.devices, DeviceKind.AudioInput);
    const currentDeviceId = this.selected.microphoneId;
    const currentGroupId = this.microphoneGroupId;
    if (isDifferentMic(newDevice, currentDeviceId, currentGroupId)) {
      Devices.getLogger().info(
        '[Devices/setMicrophone]',
        'set new microphone',
        {
          id,
          savePreference,
        },
      );

      this.setSelectedDevice('microphoneId', id, savePreference);
      this.microphoneGroupId = newDevice?.groupId;
    }
  }

  /**
   * Select the audio output device to be used and save the preference to localstorage
   * @param id speakerId
   * @param savePreference should this device be saved in localstorage
   */
  setSpeaker(id: SpeakerId, savePreference = true): void {
    Devices.getLogger().info('[Devices/setSpeaker]', { id, savePreference });

    this.setSelectedDevice('speakerId', id, savePreference);
  }

  /**
   * The media class listens for changes in the access status and calls this
   * method when that changes.
   */
  async updatedAccessStatus(): Promise<void> {
    await this.devicesChangeHandler();
  }

  private setSelectedDevice<T extends keyof SelectedDevices>(
    field: T,
    id: SelectedDevices[T],
    save: boolean,
  ): void {
    if (typeof id === 'undefined') {
      Devices.getLogger().warn(
        '[Devices/setSelectedDevice]',
        'No selected devices. Skipped',
      );
      // this is to satisfy typescript
      return;
    }

    Devices.getLogger().info('[Devices/setSelectedDevice]', {
      field,
      id,
      save,
    });

    if (save) {
      this.setStoredDevice(field, id);
    }

    this._selected[field] = id;
    let explicitDevice = undefined;
    if (field === 'microphoneId') {
      explicitDevice = getExplicitIdForGroup(
        findDeviceById(id, this.devices, DeviceKind.AudioInput),
        this.devices,
        DeviceKind.AudioInput,
      );
    }
    // this event listener is only used for vonage for now.
    const event: SelectedDevicesChangeEvent = {
      type: MediaEventType.SelectedDevicesChange,
      data: {
        field,
        value: id,
        explicitId: explicitDevice?.deviceId as MicrophoneId,
      },
    };
    this.fire([event]);
    Devices.getLogger().info('[Devices/setSelectedDevice]', 'Done', {
      field,
      id,
      save,
    });
  }

  private async devicesChangeHandler(): Promise<void> {
    Devices.getLogger().info('[Devices/devicesChangeHandler]');

    const devices = await Devices.enumerateDevices();
    this._devices = devices;
    if (devices.length === 0) {
      Devices.getLogger().warn(
        '[Devices/devicesChangeHandler]',
        'No media devices found. Skipped',
      );
      return;
    }
    const devicesHash = devices.map((d) => d.label).join('-');

    // in case this was unset we want to restore previously selected devices
    if (!this.devicesHash) {
      Devices.getLogger().info(
        '[Devices/devicesChangeHandler]',
        'restore selected devices',
      );

      this.devicesHash = devicesHash;
      this.restoreSelectedDevices();
      this.selectDefaultDevices();
      this.fire([{ type: MediaEventType.DevicesChange }]);
      return;
    }

    if (this.devicesHash !== devicesHash) {
      Devices.getLogger().info(
        '[Devices/devicesChangeHandler]',
        'clear selected devices',
      );

      this.devicesHash = devicesHash;
      this.clearRemovedSelectedDevices();
      this.selectDefaultDevices();
      this.fire([{ type: MediaEventType.DevicesChange }]);
      // default and communications are device ids that allow the OS to manage microphones.
      // communications is specific to windows
      if (
        this.selected.microphoneId === 'default' ||
        this.selected.microphoneId === 'communications'
      ) {
        this.setMicrophone(this.selected.microphoneId);
      }
    }
  }

  /**
   * Sets the selected devices to match the previously saved selections in localStorage.
   *
   * _Note:_ Some browsers with strict privacy settings generate new `deviceId`s
   * for the same devices in every session to prevent fingerprinting.
   * A `deviceId` is checked for existence in the list of the currently available
   * devices first to prevent issues that may arise from requesting a media track
   * from a non-existent device.
   */
  private restoreSelectedDevices(): void {
    Devices.getLogger().info('[Devices/restoreSelectedDevices]');

    const devices = this.devices;
    const stored = storage.getItem<Partial<SelectedDevices>>(
      MediaStorageKeys.SelectedDevices,
      true,
    );
    if (stored === undefined) {
      Devices.getLogger().warn(
        '[Devices/restoreSelectedDevices]',
        'No stored devices. Skipped',
      );

      return;
    }
    const { cameraId, microphoneId, speakerId } = stored as SelectedDevices;

    Devices.getLogger().info(
      '[Devices/restoreSelectedDevices]',
      'selected devices IDs:',
      { cameraId, microphoneId, speakerId },
    );

    if (cameraId && hasDevice(cameraId, devices)) {
      this.setCamera(cameraId, false);
    }
    if (microphoneId && hasDevice(microphoneId, devices)) {
      this.setMicrophone(microphoneId, false);
    }
    if (speakerId && hasDevice(speakerId, devices)) {
      this.setSpeaker(speakerId, false);
    }
  }

  /**
   * When devices change because they get (un)plugged from the system
   * we want to remove any selected devices that don't exist anymore.
   * The selectDefaultDevices() method can then be used to select the
   * default or next available device
   */
  private clearRemovedSelectedDevices(): void {
    Devices.getLogger().info('[Devices/clearRemovedSelectedDevices]');

    if (!shouldKeepDeviceSelected(this.selected.cameraId, this._devices)) {
      this._selected.cameraId = undefined;
    }
    if (!shouldKeepDeviceSelected(this.selected.microphoneId, this._devices)) {
      this._selected.microphoneId = undefined;
    }
    if (!shouldKeepDeviceSelected(this.selected.speakerId, this._devices)) {
      this._selected.speakerId = undefined;
    }
  }

  /**
   * If there is an empty device we want to either select
   * the default one or select the first device.
   */
  private selectDefaultDevices(): void {
    Devices.getLogger().info('[Devices/selectDefaultDevices]');

    if (!this.selected.microphoneId) {
      this.setMicrophone(
        findDefaultDevice(this._devices, DeviceKind.AudioInput) as MicrophoneId,
        false,
      );
    }
    if (!this.selected.speakerId) {
      this.setSpeaker(
        findDefaultDevice(this._devices, DeviceKind.AudioOutput) as SpeakerId,
        false,
      );
    }
    if (!this.selected.cameraId) {
      this.setCamera(
        findDefaultDevice(this._devices, DeviceKind.VideoInput) as CameraId,
        false,
      );
    }
  }

  /**
   * Save the current selection to localstorage
   */
  private setStoredDevice(key: string, value: string): void {
    const stored =
      storage.getItem<Partial<SelectedDevices>>(
        MediaStorageKeys.SelectedDevices,
        true,
      ) ?? {};
    storage.setItem(
      MediaStorageKeys.SelectedDevices,
      { ...stored, [key]: value },
      true,
    );
  }
}
